/**
 * Point d'entrée web.
 *
 * Ce fichier ne contient que du câblage : il crée l'état, les actions, le
 * routeur et les pages, puis les relie. Aucune logique métier ici.
 *
 * C'est aussi le seul endroit à adapter pour la coque mobile, et seulement
 * pour ajouter les appels de cycle de vie propres à Capacitor (masquage de
 * l'écran de démarrage, retour du bouton physique).
 */
import 'leaflet/dist/leaflet.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';

import { REFRESH_INTERVAL_MS, RERENDER_INTERVAL_MS } from './core/constants';
import { ageInMinutes } from './core/format';
import { createActions } from './state/actions';
import { createStore } from './state/store';
import { startAnalytics } from './ui/analytics';
import { html, render } from './ui/html';
import { createHomePage } from './ui/pages/home';
import { createMapPage } from './ui/pages/map';
import { renderSourcesPage } from './ui/pages/sources';
import { createRouter, type RouteName } from './ui/router';
import { createShell } from './ui/shell';

function required<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id) as T | null;
  if (!element) throw new Error(`Élément introuvable : #${id}`);
  return element;
}

const views: Record<RouteName, HTMLElement> = {
  home: required('view-home'),
  map: required('view-map'),
  sources: required('view-sources'),
};

const homeContent = required('home-content');
const sourcesContent = required('sources-content');

const store = createStore();
const actions = createActions(store);
const router = createRouter();

// La carte est créée avant l'accueil : celui-ci lui délègue la sélection d'un
// feu quand on tape sur une ligne de la liste.
const mapPage = createMapPage(required('map'), required('zone-card'));

const homePage = createHomePage(homeContent, {
  actions,
  onZoneSelected(zoneId) {
    router.go('map');
    mapPage.focusZone(store.get(), zoneId);
  },
});

const shell = createShell({ actions });

let sourcesRendered = false;

/** Redessine la vue courante. Les autres attendront d'être affichées. */
function paint(): void {
  const state = store.get();
  shell.render(state);
  switch (router.current()) {
    case 'home':
      homePage.render(state);
      break;
    case 'map':
      mapPage.render(state);
      break;
    case 'sources':
      break;
  }
}

router.onChange((route) => {
  for (const name of Object.keys(views) as RouteName[]) {
    views[name].dataset['active'] = String(name === route);
  }
  shell.setRoute(route);

  const state = store.get();
  if (route === 'home') homePage.render(state);
  if (route === 'map') mapPage.activate(state);
  if (route === 'sources' && !sourcesRendered) {
    renderSourcesPage(sourcesContent);
    sourcesRendered = true;
  }
});

store.subscribe(() => paint());

/*
 * Une erreur inattendue ne doit pas laisser un écran vide sans explication :
 * l'utilisateur doit au moins savoir qu'il peut recharger.
 */
window.addEventListener('error', (event) => {
  if (homeContent.childElementCount > 0) return;
  render(
    homeContent,
    html`
      <h2 class="verdict" style="--verdict-color: var(--mid)">Un instant</h2>
      <p class="verdict-reason">
        Les informations se chargent. Si rien ne s'affiche, recharge la page.
      </p>
      <p class="hint">${event.message}</p>
    `,
  );
});

router.start();

/* ---------------------------------------------------------------------------
   Cycles de rafraîchissement

   Contrairement à la version monofichier, le balayage satellite ne dépend plus
   de la carte : il tourne même si l'utilisateur ne quitte jamais l'accueil.
   Avant, les trois déclencheurs étaient conditionnés à l'existence de l'objet
   carte, et les feux n'étaient donc balayés qu'une fois par chargement.
--------------------------------------------------------------------------- */

setInterval(() => {
  void actions.refresh();
  void actions.runScan();
  /*
   * Réessai des surfaces brûlées, sans coût quand tout va bien.
   *
   * `loadBurntAreas` ne fait rien une fois qu'elle a réussi. L'appeler à chaque
   * cycle rattrape donc les défaillances passagères d'EFFIS, qui en a, et
   * s'arrête définitivement dès qu'une réponse arrive.
   */
  void actions.loadBurntAreas();
}, REFRESH_INTERVAL_MS);

// Les heures d'arrivée de fumée doivent rester justes sans rien recharger.
setInterval(() => paint(), RERENDER_INTERVAL_MS);

document.addEventListener('visibilitychange', () => {
  if (document.hidden) return;
  const last = store.get().lastRefresh;
  if (last !== null && ageInMinutes(last, Date.now()) <= 2) return;
  void actions.refresh();
  void actions.runScan();
});

window.addEventListener('online', () => {
  void actions.refresh();
  void actions.runScan();
});

void actions.boot();

/*
 * Les surfaces brûlées viennent après le reste, et une seule fois.
 *
 * C'est du contexte de carte, pas la réponse à la question posée : elles ne
 * doivent pas disputer la bande passante au vent, à l'air et aux feux actifs,
 * qui eux décident du verdict. EFFIS ne publie de toute façon ses périmètres
 * qu'après coup.
 */
setTimeout(() => void actions.loadBurntAreas(), 3000);

// Sans cookie, sans donnée personnelle, et seulement si un jeton est fourni
// à la compilation. Volontairement en dernier : rien ne doit passer avant
// l'affichage du verdict.
startAnalytics();
